import { IPaymentSchedule } from "@/models/paymentSchedule/paymentSchedule.types"
import { PaymentScheduleParser } from "@/models/paymentSchedule/paymentSchedule.parser"
import { QueryResult } from "@/api/utils/types"
import { useQuery } from "@/api/utils/useQuery"
import { mapQueryResult } from "@/api/utils/mapQuery"
import {
  AggregatedQueryProps,
  QueryAggregator,
} from "@/api/utils/QueryAggregator"
import { AccountService } from "./AccountService"
import { PaymentScheduleDetails } from "@/api/backofficeRest/generated/model"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"

export interface IPaymentScheduleService {
  getForCoreId(coreId: number): QueryResult<IPaymentSchedule[]>
  getForAccount(accountNumber: string): AggregatedQueryProps<IPaymentSchedule[]>
  getHistoricSchedules(
    accountNumber: string,
  ): AggregatedQueryProps<IPaymentSchedule[] | undefined>
  getCurrentSchedule(
    accountNumber: string,
  ): AggregatedQueryProps<IPaymentSchedule | undefined>
  getNextSchedule(
    accountNumber: string,
  ): AggregatedQueryProps<IPaymentSchedule | undefined>
  getFutureSchedules(
    accountNumber: string,
  ): AggregatedQueryProps<IPaymentSchedule[] | undefined>
}

class PaymentScheduleServiceClass implements IPaymentScheduleService {
  getForCoreId(coreId: number): QueryResult<IPaymentSchedule[]> {
    return useQuery<PaymentScheduleDetails, IPaymentSchedule[]>({
      ...BackofficeRestQueries.payments.scheduleDetails(coreId),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (response) =>
        (response ?? []).map((item) => PaymentScheduleParser.toDomain(item)),
    })
  }

  getHistoricSchedules(accountNumber: string) {
    return mapQueryResult(this.getForAccount(accountNumber), (query) =>
      query.data
        .filter((schedule) => !schedule.isActive)
        .toSorted((a, b) =>
          a.nextPaymentDt.isAfter(b.nextPaymentDt) ? 1 : -1,
        ),
    )
  }

  getForAccount(accountNumber: string) {
    const queries = new QueryAggregator()

    const coreIdQuery = AccountService.getCoreIdByAccountNumber(accountNumber)
    queries.add(coreIdQuery)

    if (!coreIdQuery.data) {
      return queries.setData([]).getAggregate()
    }

    const getForCoreIdQuery = this.getForCoreId(coreIdQuery.data)
    queries.add(getForCoreIdQuery)

    return queries.setData(getForCoreIdQuery.data ?? []).getAggregate()
  }

  getCurrentSchedule(accountNumber: string) {
    return mapQueryResult(
      this.getForAccount(accountNumber),
      (query) =>
        query.data
          .filter((schedule) => schedule.isActive)
          .toSorted((a, b) =>
            a.nextPaymentDt.isAfter(b.nextPaymentDt) ? 1 : -1,
          )[0],
    )
  }

  getNextSchedule(accountNumber: string) {
    return mapQueryResult(this.getForAccount(accountNumber), (query) => {
      const activeSchedules = query.data.filter((schedule) => schedule.isActive)

      if (activeSchedules.length > 1) {
        return activeSchedules.toSorted((a, b) =>
          a.nextPaymentDt.isAfter(b.nextPaymentDt) ? 1 : -1,
        )[0]
      }

      return activeSchedules[0]
    })
  }

  getFutureSchedules(accountNumber: string) {
    return mapQueryResult(this.getForAccount(accountNumber), (query) =>
      query.data
        .filter((schedule) => schedule.isActive)
        .toSorted((a, b) => (a.nextPaymentDt.isAfter(b.nextPaymentDt) ? 1 : -1))
        .slice(1),
    )
  }

  hasActivePaymentSchedule(accountNumber: string) {
    return mapQueryResult(this.getForAccount(accountNumber), (query) =>
      query.data.some((schedule) => schedule.isActive),
    )
  }
}

export const PaymentScheduleService = new PaymentScheduleServiceClass()
